import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Table, OverlayTrigger, Tooltip, Card, Modal, Form, Spinner, Alert, InputGroup } from "react-bootstrap";
import { AiOutlinePlus, AiOutlineEdit, AiOutlineTool, AiOutlineSafetyCertificate, AiOutlineCheckCircle, AiOutlinePauseCircle, AiOutlineSetting, AiOutlineCreditCard, AiOutlineSearch } from "react-icons/ai";
import axios from "axios";
import { formatDateCompact, expiryTextClass } from "./mockFleet";
import VehicleUsageRejectedAlert from "../../../components/VehicleUsageRejectedAlert";

const STATUS_KEYS = { active: "statusActive", inactive: "statusInactive", maintenance: "statusMaintenance", under_payment: "statusUnderPayment" };

const FleetList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "plate", direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [installmentForm, setInstallmentForm] = useState({
    vehicleId: "",
    initialDeposit: "",
    totalPrice: "",
    pricePerWeek: ""
  });
  const [installmentSubmitting, setInstallmentSubmitting] = useState(false);
  const [installmentError, setInstallmentError] = useState("");
  const [installmentErrors, setInstallmentErrors] = useState({});

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  useEffect(() => {
    axios
      .get("/api/fleet/vehicles", authHeaders())
      .then((res) => setVehicles(res.data || []))
      .catch((err) => setError(err.response?.data?.error || err.message || t("fleet.loadError")))
      .finally(() => setLoading(false));
  }, []);

  const formatDatesCell = (v) => {
    const oc = formatDateCompact(v.insuranceEndDate);
    const to = formatDateCompact(v.toEndDate);
    const ocClass = expiryTextClass(v.insuranceEndDate);
    const toClass = expiryTextClass(v.toEndDate);
    return (
      <div className="row g-1">
        <div className="col-6">
          <OverlayTrigger placement="top" overlay={<Tooltip>{t("fleet.insuranceOc")}</Tooltip>}>
            <Card
              className={`small border-0 shadow-sm py-1 px-2 fw-bold ${ocClass}`}
              style={{ cursor: "help" }}
            >
              <Card.Body className="p-0 d-flex align-items-center gap-1">
                <AiOutlineSafetyCertificate size={16} />
                <span>{oc}</span>
              </Card.Body>
            </Card>
          </OverlayTrigger>
        </div>
        <div className="col-6">
          <OverlayTrigger placement="top" overlay={<Tooltip>{t("fleet.technicalReview")}</Tooltip>}>
            <Card
              className={`small border-0 shadow-sm py-1 px-2 fw-bold ${toClass}`}
              style={{ cursor: "help" }}
            >
              <Card.Body className="p-0 d-flex align-items-center gap-1">
                <AiOutlineTool size={16} />
                <span>{to}</span>
              </Card.Body>
            </Card>
          </OverlayTrigger>
        </div>
      </div>
    );
  };

  const filteredVehicles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return vehicles;
    const qCompact = q.replace(/\s+/g, "");
    return vehicles.filter((v) => {
      const plate = (v.plate || "").toLowerCase();
      const driver = (v.driverName || "").toLowerCase();
      const plateCompact = plate.replace(/\s+/g, "");
      return plate.includes(q) || driver.includes(q) || (qCompact && plateCompact.includes(qCompact));
    });
  }, [vehicles, searchQuery]);

  const sortedVehicles = useMemo(() => {
    const key = sortConfig.key;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return [...filteredVehicles].sort((a, b) => {
      const va = a[key];
      const vb = b[key];
      if (va == null && vb == null) return 0;
      if (va == null) return dir;
      if (vb == null) return -dir;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filteredVehicles, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const statusVariant = (status) => {
    if (status === "active") return "success";
    if (status === "maintenance") return "warning";
    if (status === "under_payment") return "info";
    return "secondary";
  };

  const statusIcon = (status) => {
    if (status === "active") return AiOutlineCheckCircle;
    if (status === "maintenance") return AiOutlineSetting;
    if (status === "under_payment") return AiOutlineCreditCard;
    return AiOutlinePauseCircle;
  };

  const installmentTotal = useMemo(() => {
    const deposit = Number(installmentForm.initialDeposit) || 0;
    const totalPrice = Number(installmentForm.totalPrice) || 0;
    const pricePerWeek = Number(installmentForm.pricePerWeek) || 0;
    const remaining = Math.max(0, totalPrice - deposit);
    const weeks = pricePerWeek > 0 ? Math.ceil(remaining / pricePerWeek) : 0;
    const instalments = weeks * pricePerWeek;
    return { deposit, totalPrice, remaining, weeks, instalments };
  }, [installmentForm.initialDeposit, installmentForm.totalPrice, installmentForm.pricePerWeek]);

  const handleInstallmentChange = (e) => {
    const { name, value } = e.target;
    setInstallmentForm((prev) => ({ ...prev, [name]: value }));
    if (installmentErrors[name]) setInstallmentErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateInstallmentForm = () => {
    const { vehicleId, totalPrice, initialDeposit, pricePerWeek } = installmentForm;
    const errs = {};
    if (!vehicleId) errs.vehicleId = t("fleet.errorSelectVehicle");
    const total = Number(totalPrice);
    const deposit = Number(initialDeposit);
    const perWeek = Number(pricePerWeek);
    if (total < 0 || isNaN(total)) errs.totalPrice = t("fleet.errorTotalPrice");
    if (deposit < 0 || isNaN(deposit)) errs.initialDeposit = t("fleet.errorInitialDeposit");
    if (perWeek <= 0 || isNaN(perWeek)) errs.pricePerWeek = t("fleet.errorPricePerWeek");
    if (!errs.totalPrice && !errs.initialDeposit && deposit > total) errs.initialDeposit = t("fleet.errorDepositExceedsTotal");
    setInstallmentErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleInstallmentSubmit = (e) => {
    e.preventDefault();
    setInstallmentError("");
    if (!validateInstallmentForm()) return;
    setInstallmentSubmitting(true);
    const { vehicleId, totalPrice, initialDeposit, pricePerWeek } = installmentForm;
    axios
      .post(
        "/api/fleet/installments",
        {
          vehicle_id: Number(vehicleId),
          total_price: Number(totalPrice) || 0,
          initial_deposit: Number(initialDeposit) || 0,
          price_per_week: Number(pricePerWeek) || 0
        },
        authHeaders()
      )
      .then(() => {
        setShowInstallmentModal(false);
        setInstallmentForm({ vehicleId: "", initialDeposit: "", totalPrice: "", pricePerWeek: "" });
        setInstallmentErrors({});
        return axios.get("/api/fleet/vehicles", authHeaders());
      })
      .then((res) => setVehicles(res.data || []))
      .catch((err) => setInstallmentError(err.response?.data?.error || err.message || t("fleet.saveError")))
      .finally(() => setInstallmentSubmitting(false));
  };

  const openInstallmentModal = () => {
    setInstallmentForm({ vehicleId: "", initialDeposit: "", totalPrice: "", pricePerWeek: "" });
    setInstallmentErrors({});
    setInstallmentError("");
    setShowInstallmentModal(true);
  };

  const renderPlateWithStatus = (v) => {
    const Icon = statusIcon(v.status);
    const variant = statusVariant(v.status);
    const label = t(`fleet.${STATUS_KEYS[v.status] || "statusActive"}`);
    return (
      <span className="d-inline-flex align-items-center gap-2">
        <span className="fw-semibold">{v.plate}</span>
        <OverlayTrigger placement="top" overlay={<Tooltip>{label}</Tooltip>}>
          <span className={`text-${variant}`} style={{ cursor: "help" }}>
            <Icon size={18} />
          </span>
        </OverlayTrigger>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4 px-4 d-flex align-items-center justify-content-center" style={{ minHeight: "200px" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 px-4">
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      <VehicleUsageRejectedAlert className="mb-3" />
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h2 className="mb-0">{t("fleet.title")}</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate("/admin/fleet/usage-logs")}>
            {t("fleet.usageLogPageTitle")}
          </Button>
          <Button variant="outline-primary" onClick={openInstallmentModal}>
            <AiOutlineCreditCard className="me-2" />
            {t("fleet.autoUnderPayment")}
          </Button>
          <Button variant="primary" onClick={() => navigate("/admin/fleet/new")}>
            <AiOutlinePlus className="me-2" />
            {t("fleet.addVehicle")}
          </Button>
        </div>
      </div>

      <Form.Group className="mb-3" controlId="fleet-search">
        <InputGroup>
          <InputGroup.Text aria-hidden>
            <AiOutlineSearch aria-hidden />
          </InputGroup.Text>
          <Form.Control
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("fleet.searchPlaceholder")}
            autoComplete="off"
            aria-label={t("fleet.searchPlaceholder")}
          />
        </InputGroup>
      </Form.Group>

      <div className="table-responsive">
        <Table bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("plate")}>
                {t("fleet.plate")} {sortConfig.key === "plate" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th>
                <span className="d-inline-flex align-items-center gap-1">
                  <OverlayTrigger placement="top" overlay={<Tooltip>{t("fleet.insuranceOc")}</Tooltip>}>
                    <span style={{ cursor: "help" }}><AiOutlineSafetyCertificate size={18} /></span>
                  </OverlayTrigger>
                  <span className="text-muted">/</span>
                  <OverlayTrigger placement="top" overlay={<Tooltip>{t("fleet.technicalReview")}</Tooltip>}>
                    <span style={{ cursor: "help" }}><AiOutlineTool size={18} /></span>
                  </OverlayTrigger>
                </span>
              </th>
              <th>{t("fleet.driver")}</th>
              <th className="text-center" style={{ width: "56px" }} />
            </tr>
          </thead>
          <tbody>
            {sortedVehicles.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-muted text-center py-4">
                  {vehicles.length === 0 ? t("fleet.noVehicles") : t("fleet.noSearchResults")}
                </td>
              </tr>
            ) : (
              sortedVehicles.map((v) => (
                <tr key={v.id}>
                  <td>{renderPlateWithStatus(v)}</td>
                  <td>{formatDatesCell(v)}</td>
                  <td>{v.driverName || "–"}</td>
                  <td className="text-center">
                    <OverlayTrigger placement="left" overlay={<Tooltip>{t("fleet.editVehicle")}</Tooltip>}>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-primary"
                        onClick={() => navigate(`/admin/fleet/${v.id}`)}
                        aria-label={t("fleet.editVehicle")}
                      >
                        <AiOutlineEdit size={20} />
                      </Button>
                    </OverlayTrigger>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showInstallmentModal} onHide={() => setShowInstallmentModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("fleet.modalUnderPaymentTitle")}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleInstallmentSubmit}>
          <Modal.Body>
            {installmentError && <Alert variant="danger" className="mb-2">{installmentError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>{t("fleet.vehicle")} *</Form.Label>
              <Form.Select
                name="vehicleId"
                value={installmentForm.vehicleId}
                onChange={handleInstallmentChange}
                isInvalid={!!installmentErrors.vehicleId}
              >
                <option value="">{t("fleet.selectVehicle")}</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{v.plate} {v.driverName ? `(${v.driverName})` : ""}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{installmentErrors.vehicleId}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("fleet.initialDeposit")} *</Form.Label>
              <Form.Control
                type="number"
                name="initialDeposit"
                value={installmentForm.initialDeposit}
                onChange={handleInstallmentChange}
                min={0}
                step={100}
                placeholder="0"
                isInvalid={!!installmentErrors.initialDeposit}
              />
              <Form.Control.Feedback type="invalid">{installmentErrors.initialDeposit}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("fleet.totalToPay")} *</Form.Label>
              <Form.Control
                type="number"
                name="totalPrice"
                value={installmentForm.totalPrice}
                onChange={handleInstallmentChange}
                min={0}
                step={100}
                placeholder="np. 50000"
                isInvalid={!!installmentErrors.totalPrice}
              />
              <Form.Control.Feedback type="invalid">{installmentErrors.totalPrice}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("fleet.pricePerWeek")} *</Form.Label>
              <Form.Control
                type="number"
                name="pricePerWeek"
                value={installmentForm.pricePerWeek}
                onChange={handleInstallmentChange}
                min={0}
                step={10}
                placeholder="np. 500"
                isInvalid={!!installmentErrors.pricePerWeek}
              />
              <Form.Control.Feedback type="invalid">{installmentErrors.pricePerWeek}</Form.Control.Feedback>
            </Form.Group>
            {(installmentForm.totalPrice || installmentForm.pricePerWeek || installmentForm.initialDeposit) && (
              <div className="small bg-light rounded p-2 mb-2">
                <div>{t("fleet.remainingAfterDeposit")}: <strong>{installmentTotal.remaining.toLocaleString("pl-PL")} PLN</strong></div>
                <div><strong>{t("fleet.weeksCount")}:</strong> {installmentTotal.weeks} × {installmentForm.pricePerWeek || 0} PLN = <strong>{installmentTotal.instalments.toLocaleString("pl-PL")} PLN</strong></div>
                <div>{t("fleet.totalWithRates")}: <strong>{installmentForm.totalPrice ? Number(installmentForm.totalPrice).toLocaleString("pl-PL") : "0"} PLN</strong></div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowInstallmentModal(false)} disabled={installmentSubmitting}>
              {t("cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={installmentSubmitting}>
              {installmentSubmitting ? t("fleet.saving") : t("fleet.saveUnderPayment")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default FleetList;
